"use client";

import { Link } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Twitter, Linkedin, Facebook, Instagram } from "lucide-react";
import { ThemeToggle } from "./theme-toggle";
import { useTheme } from "./theme-provider";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const ListItem = ({ className, title, href, children }: {
  className?: string;
  title: string;
  href: string;
  children: React.ReactNode;
}) => {
  return (
    <li className="relative z-[99999]">
      <NavigationMenuLink asChild>
        <Link
          to={href}
          className={cn(
            "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors group",
            className
          )}
        >
          <div className="text-sm font-medium leading-none text-foreground">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground group-hover:text-foreground transition-colors">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  );
};

export function Header1() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const handleNavigation = (path: string) => {
    setIsOpen(false); // Close menu when navigating
    navigate(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-[99999] bg-background isolate">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 bg-background relative z-[99999]">
        <div className="flex h-24 items-center justify-between bg-background">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img 
                src={isDarkMode ? "/LOGO.png" : "/LOGOLIGHT.png"}
                alt="VYNTRA Logo"
                className="h-16 w-auto sm:h-20"
                style={{ transition: 'opacity 0.3s ease' }}
              />
            </Link>
          </div>

          {/* Hidden Mobile menu button - kept in code but not displayed */}
          <div className="hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-[#4D5B7C] hover:text-[#000c2a]/80"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Desktop Navigation - Hidden but kept in code */}
          <div className="hidden">
            <NavigationMenu className="relative z-[99999]">
              {/* Navigation links hidden */}
            </NavigationMenu>
          </div>

          {/* Right section: Theme Toggle + Social Media Icons + Join Waitlist Button */}
          <div className="flex items-center space-x-6">
            {/* Theme Toggle - always visible */}
            <ThemeToggle />
            
            {/* Social Media Icons - hidden on mobile */}
            <div className="hidden md:flex space-x-4">
              <a href="https://x.com/Sentiriuk" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-[#00DC82] transition-colors">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://www.linkedin.com/company/sentiri" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-[#00DC82] transition-colors">
                <Linkedin className="h-5 w-5" />
              </a>
              <a href="https://www.facebook.com/share/1WFwJRjDue/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-[#00DC82] transition-colors">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/sentiriuk/" target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-[#00DC82] transition-colors">
                <Instagram className="h-5 w-5" />
              </a>
            </div>

            {/* Join Waitlist Button - always visible */}
            <div className="block">
              <Link
                to="/waitlist"
                className="rounded-md bg-[#00DC82] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#00DC82]/90"
              >
                Join Waitlist
              </Link>
            </div>
          </div>

          {/* Mobile Navigation overlay - Hidden but kept in code */}
          {false && isOpen && (
            <div className="absolute top-16 left-0 right-0 bg-background md:hidden z-[99999] shadow-lg">
              {/* Mobile menu content hidden */}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}